import {Component, lazy, type ReactNode, Suspense} from "react";
import {useMessages} from "i18n";
import {CloseGameButton} from "./CloseGameButton";
import type {GameId} from "./gameCatalog";
import {GAME_SLOT_HEIGHTS} from "./gameGeometry";
import type {TransferGameProps} from "./gameProps";
import {GameFallback} from "./GameSlot.styles";

/**
 * The slot the open game renders in (kitecorp/transferify-ui#2872, #2874, #2876, #2873). Each game is its own
 * DYNAMIC import, fetched only when somebody presses Play on it, so no game's canvas, engine or
 * painter is in any pre-rendered page's eager graph, and the slot loads only the game it picked.
 */
const GAMES: Record<GameId, React.LazyExoticComponent<(props: TransferGameProps) => React.JSX.Element>> = {
	byte_runner: lazy(() => import("./byteRunner/ByteRunnerGame")),
	file_invaders: lazy(() => import("./fileInvaders/FileInvadersGame")),
	pack_the_zip: lazy(() => import("./packTheZip/PackTheZipGame")),
	"2048": lazy(() => import("./game2048/Game2048")),
};

/**
 * Shows the placeholder, with its ×, when the game's chunk fails to load, for example a stale chunk
 * after a deploy. Without it the failure would take the card down with it, and an upload held on the
 * game ("Upload complete") would have no way on to its link.
 */
class GameChunkBoundary extends Component<{fallback: ReactNode; children: ReactNode}, {failed: boolean}> {
	state = {failed: false};

	static getDerivedStateFromError(): {failed: boolean} {
		return {failed: true};
	}

	render() {
		return this.state.failed ? this.props.fallback : this.props.children;
	}
}

/**
 * `statusLine` defaults to what the mode's transfer does while a round is lost ("Your upload is still
 * going."); the recipient page passes its own, because its line follows the download
 * (kitecorp/transferify-ui#2889).
 */
export function GameStage({game, statusLine, ...props}: Omit<TransferGameProps, "statusLine"> & {game: GameId; statusLine?: string}) {
	const {games} = useMessages();
	const Game = GAMES[game];
	const height = GAME_SLOT_HEIGHTS[game];
	// The placeholder carries the ×, so the game can be closed while its chunk loads, or if it never does.
	const placeholder = (
		<GameFallback $height={height}>
			<CloseGameButton onClose={props.onClose}/>
		</GameFallback>
	);
	return (
		<GameChunkBoundary fallback={placeholder}>
			<Suspense fallback={placeholder}>
				<Game {...props} statusLine={statusLine ?? games.common.stillGoing[props.mode]}/>
			</Suspense>
		</GameChunkBoundary>
	);
}
